import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  TextField,
  TextFieldProps,
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

export type FormMentionTextFieldProps = Omit<TextFieldProps, 'name'> & {
  name: string;
  people: Array<{ id: string; name: string }>;
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

export const FormMentionTextField: React.FC<FormMentionTextFieldProps> = ({
  name,
  label,
  people,
  ...rest
}) => {
  const { control } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const onChangeRef = useRef<((value: any) => void) | null>(null);

  const suggestions = useMemo(() => {
    if (!query) {
      return people;
    }
    const q = query.toLowerCase();
    return people.filter((p) => p.name.toLowerCase().includes(q));
  }, [people, query]);

  // Handle activeIndex adjustment if suggestions change
  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions.length]);

  const handleTextSelectionAndQuery = (target: HTMLInputElement | HTMLTextAreaElement) => {
    const text = target.value;
    const caretPos = target.selectionStart ?? 0;
    const prefix = text.substring(0, caretPos);
    const lastAt = prefix.lastIndexOf('@');

    if (lastAt !== -1 && (lastAt === 0 || /\s/.test(prefix[lastAt - 1]))) {
      const queryPart = prefix.substring(lastAt + 1);
      if (!/\s/.test(queryPart)) {
        setIsOpen(true);
        setQuery(queryPart);
        setMentionStartIndex(lastAt);
        setAnchorEl(target);
        return;
      }
    }
    setIsOpen(false);
    setQuery('');
  };

  const handleSelect = (person: { id: string; name: string }) => {
    if (!inputRef.current) return;
    const target = inputRef.current;
    const text = target.value;
    const caretPos = target.selectionStart ?? 0;

    const prefix = text.substring(0, mentionStartIndex);
    const suffix = text.substring(caretPos);
    const inserted = `@${person.name} `;
    const newValue = prefix + inserted + suffix;

    if (onChangeRef.current) {
      onChangeRef.current(newValue);
    }

    setIsOpen(false);
    setQuery('');

    setTimeout(() => {
      target.focus();
      const newCursorPos = mentionStartIndex + inserted.length;
      target.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const selected = suggestions[activeIndex];
      if (selected) {
        handleSelect(selected);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ref, ...fieldRest }, fieldState: { error } }) => {
        onChangeRef.current = onChange;
        return (
          <div style={{ position: 'relative', width: '100%' }}>
            <TextField
              {...fieldRest}
              value={value ?? ''}
              onChange={(e) => {
                onChange(e);
                handleTextSelectionAndQuery(e.target);
              }}
              onKeyUp={(e) => {
                handleTextSelectionAndQuery(e.target as HTMLInputElement);
              }}
              onSelect={(e) => {
                handleTextSelectionAndQuery(e.target as HTMLInputElement);
              }}
              onKeyDown={handleKeyDown}
              inputRef={(el) => {
                inputRef.current = el;
                ref(el);
              }}
              label={label}
              error={!!error}
              helperText={error?.message}
              {...rest}
            />
            <Popper
              open={isOpen && suggestions.length > 0}
              anchorEl={anchorEl}
              placement="bottom-start"
              style={{ zIndex: 1400, width: anchorEl ? anchorEl.clientWidth : undefined }}
            >
              <Paper elevation={3} style={{ maxHeight: 200, overflow: 'auto', marginTop: 4 }}>
                <List dense>
                  {suggestions.map((person, index) => (
                    <ListItemButton
                      key={person.id}
                      selected={index === activeIndex}
                      onClick={() => handleSelect(person)}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>
                          {getInitials(person.name)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={person.name} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Popper>
          </div>
        );
      }}
    />
  );
};
